/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AnyoGridComponent } from 'projects/app-core/src/app/models/AnyoGridComponent';
import { AuthService } from 'projects/app-core/src/auth/auth.service';
import { GridResponse } from 'projects/app-core/src/models/gridResponse';
import { Observable } from 'rxjs';
import { RuleEngineFacts } from '../../../models/masterDataResponse';
import { LyfisService } from '../../../services/lyfis.service';
import { MasterdataService } from '../../../services/masterdata.service';
import {
  AnyoSortDirection,
  LyfisAnswerType,
  LyfisQuestion,
  LyfisQuestionsDateFilter,
  LyfisQuestionsFilter,
  LyfisQuestionsGridRequest,
  LyfisQuestionsSearch,
  LyfisQuestionsSort,
} from '../models/lyfis-questions-grid.models';

@Component({
  selector: 'app-manage-lyfis-questions',
  templateUrl: './manage-lyfis-questions.component.html',
  styleUrl: './manage-lyfis-questions.component.scss',
})
export class ManageLyfisQuestionsComponent extends AnyoGridComponent<
  LyfisQuestionsSort,
  LyfisQuestionsFilter,
  LyfisQuestionsSearch,
  LyfisQuestionsDateFilter,
  LyfisQuestionsGridRequest,
  undefined,
  LyfisQuestion
> {
  // drawer + form state
  createDrawerVisible = false;
  saving = false;
  questionForm!: FormGroup;
  drawerMode: 'create' | 'view' | 'edit' = 'create';

  // filters state
  activeFilter: boolean = true;
  answerTypeOptions: { label: string; value: LyfisAnswerType }[] = [
    { label: 'Numeric', value: LyfisAnswerType.NUMERIC },
    { label: 'Options', value: LyfisAnswerType.OPTIONS },
    { label: 'Yes / No', value: LyfisAnswerType.YES_NO },
    { label: 'Derived', value: LyfisAnswerType.DERIVED },
    { label: 'Date', value: LyfisAnswerType.DATE },
    { label: 'Text Box', value: LyfisAnswerType.TEXT_BOX },
    { label: 'Array', value: LyfisAnswerType.ARRAY },
  ];
  answerTypeTemp: string[] = [];
  selectedAnswerTypes: string[] = [];
  diseaseTemp: string[] = [];
  selectedDiseaseIds: string[] = [];

  // dummy dropdown data (replace with real later)
  diseaseOptions: { id: string; name: string }[] = [];
  factOptions: { id: string; name: string }[] = [];
  private ruleEngineFactsMap = new Map<string, RuleEngineFacts>();
  private restrictedToOptionsOrYesNo = false;

  // internal label search temp
  internalLabelTemp: string = '';
  internalLabelSearch: string = '';

  // sort state for order
  orderSort: AnyoSortDirection | undefined = undefined;

  constructor(
    auth: AuthService,
    router: Router,
    route: ActivatedRoute,
    private lyfisSvc: LyfisService,
    private fb: FormBuilder,
    private masterdata: MasterdataService,
  ) {
    super(auth, router, route);
  }

  override async ready(): Promise<void> {
    this.initForm();
    this.loadFactsAndCategories();
    return super.ready();
  }

  private initForm(): void {
    this.questionForm = this.fb.group({
      id: [undefined],
      internalLabel: ['', [Validators.required]],
      label: ['', [Validators.required]],
      order: [1, [Validators.required, Validators.min(1)]],
      question: ['', [Validators.required]],
      questionDescription: [''],
      diseaseIds: [[]],
      answerType: [LyfisAnswerType.NUMERIC, [Validators.required]],
      factId: [[], [Validators.required]],
      options: this.fb.array([]),
      validation: this.fb.group({
        min: [null],
        max: [null],
        regex: [''],
      }),
      active: [true],
      cadence: this.fb.group({
        minIntervalDays: [null],
      }),
      placeholder: [''],
      arrayDefinitions: this.fb.array([]),
      allowMultiple: [false],
    });

    this.questionForm
      .get('answerType')
      ?.valueChanges.subscribe((val: LyfisAnswerType) => {
        this.onAnswerTypeChange(val);
      });

    this.questionForm
      .get('factId')
      ?.valueChanges.subscribe((selectedFactIds: string[]) => {
        this.onFactIdChange(selectedFactIds);
      });
  }

  private loadFactsAndCategories(): void {
    this.masterdata.lyfisMasterDataList().subscribe({
      next: (res) => {
        const facts = res?.RuleEngineFacts || [];
        this.factOptions = facts.map((f: RuleEngineFacts) => ({
          id: f.m_id as string,
          name: f.data,
        }));
        this.ruleEngineFactsMap.clear();
        facts.forEach((f: RuleEngineFacts) => {
          if (f?.m_id) this.ruleEngineFactsMap.set(f.m_id, f);
        });
        this.diseaseOptions =
          res?.LyfisDiseases?.map((d) => ({
            id: d.m_id,
            name: d.data,
          })) || [];
      },
    });
  }

  get answerTypeOptionsFiltered(): { label: string; value: LyfisAnswerType }[] {
    if (this.restrictedToOptionsOrYesNo) {
      return this.answerTypeOptions.filter(
        (o) =>
          o.value === LyfisAnswerType.OPTIONS ||
          o.value === LyfisAnswerType.YES_NO,
      );
    }
    return this.answerTypeOptions;
  }

  get optionsForm(): FormArray {
    return this.questionForm.get('options') as FormArray;
  }

  get arrayDefinitionsForm(): FormArray {
    return this.questionForm.get('arrayDefinitions') as FormArray;
  }


  onDefaultOptionChange(selectedIndex: number): void {
    this.optionsForm.controls.forEach((control, index) => {
      if (index !== selectedIndex) {
        control.get('isDefault')?.setValue(false, { emitEvent: false })
      }
    })
  }
  openCreateDrawer(): void {
    this.createDrawerVisible = true;
    this.drawerMode = 'create';
    this.resetForm();
    this.questionForm.enable({ emitEvent: false });
  }

  closeCreateDrawer(): void {
    this.createDrawerVisible = false;
  }

  openViewDrawer(row: LyfisQuestion): void {
    this.createDrawerVisible = true;
    this.drawerMode = 'view';
    this.resetForm();

    // Parse comma-separated factKey to factIds array
    const factIds = row.factId
      ? row.factId
        .split(',')
        .map((fk) => {
          const factOption = this.factOptions.find(
            (f) => f.id === fk.trim(),
          );
          return factOption ? factOption.id : fk.trim();
        })
        .filter((id) => id)
      : [];

    // set options from row before triggering type change adjustments
    this.optionsForm.clear();
    if (Array.isArray(row.options) && row.options.length) {
      row.options.forEach((o, idx) => {
        this.optionsForm.push(
          this.fb.group({
            key: [o.key],
            label: [o.label],
            order: [o.order ?? idx + 1],
            isDefault: [o.isDefault ?? false],
          }),
        );
      });
    } else if (row.answerType === LyfisAnswerType.YES_NO) {
      // Default Yes/No for records without options; keep key undefined so backend generates it
      this.optionsForm.push(
        this.fb.group({
          key: [undefined],
          label: ['Yes'],
          order: [1],
        }),
      );
      this.optionsForm.push(
        this.fb.group({
          key: [undefined],
          label: ['No'],
          order: [2],
        }),
      );
    }

    // set arrayDefinitions from row
    this.arrayDefinitionsForm.clear();
    if (Array.isArray(row.arrayDefinitions) && row.arrayDefinitions.length) {
      row.arrayDefinitions.forEach((def) => {
        this.arrayDefinitionsForm.push(
          this.fb.group({
            placeHolder: [def.placeHolder],
            factKey: [def.factKey],
            validation: this.fb.group({
              min: [def.validation?.min ?? null],
              max: [def.validation?.max ?? null],
              regex: [def.validation?.regex ?? ''],
            }),
          }),
        );
      });
    }

    this.questionForm.patchValue(
      {
        id: row._id,
        internalLabel: row.internalLabel,
        label: row.label,
        order: row.order,
        question: row.question,
        questionDescription: (row as any).questionDescription || '',
        answerType: row.answerType,
        factId: factIds,
        validation: row.validation || { min: null, max: null, regex: '' },
        active: row.active,
        diseaseIds: Array.isArray((row as any).diseaseIds)
          ? (row as any).diseaseIds
          : [],
        cadence: { minIntervalDays: row.cadence?.minIntervalDays ?? null },
        placeholder: row.placeholder || '',
        allowMultiple: (row as any).allowMultiple || false,
      },
      { emitEvent: false },
    );

    // apply enabling/disabling for validation/options based on type
    this.onAnswerTypeChange(row.answerType);

    // set restriction flag for display when viewing
    this.updateAnswerTypeRestrictionForFactIds(factIds, false);

    // disable for view mode
    this.questionForm.disable({ emitEvent: false });
  }

  enterEditMode(): void {
    this.drawerMode = 'edit';
    this.questionForm.enable({ emitEvent: false });
    // ensure validation controls state aligns with type
    const currentType = this.questionForm.get('answerType')
      ?.value as LyfisAnswerType;
    this.onAnswerTypeChange(currentType);
  }

  cancelEdit(): void {
    this.drawerMode = 'view';
    this.questionForm.disable({ emitEvent: false });
  }

  private onAnswerTypeChange(val: LyfisAnswerType): void {
    const validationGroup = this.questionForm.get('validation') as FormGroup;
    const placeholderControl = this.questionForm.get('placeholder');
    const allowMultipleControl = this.questionForm.get('allowMultiple');

    if (val === LyfisAnswerType.OPTIONS) {
      allowMultipleControl?.enable({ emitEvent: false })
    } else {
      allowMultipleControl?.disable({ emitEvent: false });
      allowMultipleControl?.setValue(false, { emitEvent: false });
    }
    // manage validation visibility by enabling/disabling controls
    const showValidation =
      val === LyfisAnswerType.NUMERIC || val === LyfisAnswerType.DERIVED;
    if (showValidation) {
      Object.values(validationGroup.controls).forEach((c) =>
        c.enable({ emitEvent: false }),
      );
    } else {
      validationGroup.reset(
        { min: null, max: null, regex: '' },
        { emitEvent: false },
      );
      Object.values(validationGroup.controls).forEach((c) =>
        c.disable({ emitEvent: false }),
      );
    }

    // manage placeholder field
    const showPlaceholder =
      val === LyfisAnswerType.NUMERIC || val === LyfisAnswerType.TEXT_BOX;
    if (showPlaceholder) {
      placeholderControl?.enable({ emitEvent: false });
      placeholderControl?.setValidators([Validators.required]);
    } else {
      placeholderControl?.disable({ emitEvent: false });
      placeholderControl?.clearValidators();
      placeholderControl?.setValue('');
    }
    placeholderControl?.updateValueAndValidity({ emitEvent: false });

    // manage options array
    if (val === LyfisAnswerType.OPTIONS) {
      const selectedFacts = (this.questionForm.get('factId')?.value ||
        []) as string[];
      const selectedFact =
        selectedFacts.length === 1
          ? this.ruleEngineFactsMap.get(selectedFacts[0])
          : undefined;
      // Only prefill if there are no options yet (avoid overwriting user edits)
      if (this.optionsForm.length === 0) {
        if (selectedFact?.availableOptions?.length) {
          this.prefillOptionsFromAvailableOptions(
            selectedFact.availableOptions,
          );
        } else {
          this.addOption();
        }
      }
    } else if (val === LyfisAnswerType.YES_NO) {
      // For Yes/No, if there are no options yet, seed with defaults (keys undefined)
      if (this.optionsForm.length === 0) {
        this.optionsForm.push(
          this.fb.group({
            key: [undefined],
            label: ['Yes'],
            order: [1],
          }),
        );
        this.optionsForm.push(
          this.fb.group({
            key: [undefined],
            label: ['No'],
            order: [2],
          }),
        );
      }
    } else {
      // numeric, derived, date, text_box, array → no options
      this.optionsForm.clear();
    }

    // manage arrayDefinitions
    if (val === LyfisAnswerType.ARRAY) {
      // Keep arrayDefinitions enabled - they should already be populated by onFactIdChange
      // Ensure each array definition has proper validation
      this.arrayDefinitionsForm.controls.forEach((control) => {
        const placeHolderControl = control.get('placeHolder');
        const factKeyControl = control.get('factKey');

        placeHolderControl?.setValidators([Validators.required]);
        factKeyControl?.setValidators([Validators.required]);

        placeHolderControl?.updateValueAndValidity({ emitEvent: false });
        factKeyControl?.updateValueAndValidity({ emitEvent: false });
      });
    } else {
      // Clear arrayDefinitions for non-array types
      this.arrayDefinitionsForm.clear();
    }
  }

  /**
   * Updates form validation based on current answer type
   * This method should be called when the answer type might be disabled
   */
  private updateFormValidationForCurrentAnswerType(): void {
    // Get the current answer type value, even if the field is disabled
    const currentAnswerType = this.questionForm.get('answerType')
      ?.value as LyfisAnswerType;

    if (currentAnswerType) {
      this.onAnswerTypeChange(currentAnswerType);
    }
  }

  private prefillOptionsFromAvailableOptions(
    available: Array<{ key: string; label: string }>,
  ): void {
    this.optionsForm.clear();
    available.forEach((o, idx) => {
      this.optionsForm.push(
        this.fb.group({
          key: [o.key],
          label: [o.label, [Validators.required]],
          order: [idx + 1, [Validators.required, Validators.min(1)]],
          isDefault: [idx === 0]
        }),
      );
    });
  }

  addOption(): void {
    this.optionsForm.push(
      this.fb.group({
        key: [undefined],
        label: ['', [Validators.required]],
        order: [
          this.optionsForm.length + 1,
          [Validators.required, Validators.min(1)],
        ],
        isDefault: [false]
      }),
    );
  }

  removeOption(idx: number): void {
    this.optionsForm.removeAt(idx);
  }

  shouldShowOptions(): boolean {
    const t = this.questionForm.get('answerType')?.value as LyfisAnswerType;
    return t === LyfisAnswerType.OPTIONS || t === LyfisAnswerType.YES_NO;
  }

  shouldDisableOptionsEditing(): boolean {
    const t = this.questionForm.get('answerType')?.value as LyfisAnswerType;
    return t === LyfisAnswerType.YES_NO;
  }

  shouldShowValidation(): boolean {
    const t = this.questionForm.get('answerType')?.value as LyfisAnswerType;
    return t === LyfisAnswerType.NUMERIC || t === LyfisAnswerType.DERIVED;
  }

  shouldShowPlaceholder(): boolean {
    const t = this.questionForm.get('answerType')?.value as LyfisAnswerType;
    return t === LyfisAnswerType.NUMERIC || t === LyfisAnswerType.TEXT_BOX;
  }

  shouldShowArrayDefinitions(): boolean {
    const t = this.questionForm.get('answerType')?.value as LyfisAnswerType;
    return t === LyfisAnswerType.ARRAY;
  }

  shouldShowAllowMultiple(): boolean {
    const t = this.questionForm.get('answerType')?.value as LyfisAnswerType
    return t === LyfisAnswerType.OPTIONS
  }

  shouldShowIsDefaultOptions(): boolean {
    const t = this.questionForm.get('answerType')?.value as LyfisAnswerType
    return t === LyfisAnswerType.OPTIONS
  }


  onFactIdChange(selectedFactIds: string[]): void {
    if (selectedFactIds.length >= 2) {
      // Auto-select array type and disable dropdown
      this.questionForm.patchValue(
        { answerType: LyfisAnswerType.ARRAY },
        { emitEvent: false },
      );
      this.questionForm.get('answerType')?.disable({ emitEvent: false });

      // Clear and rebuild arrayDefinitions
      this.rebuildArrayDefinitions(selectedFactIds);

      // Update form validation for ARRAY type
      this.updateFormValidationForCurrentAnswerType();
      // Restriction not applicable for array type
      this.restrictedToOptionsOrYesNo = false;
    } else if (selectedFactIds.length === 1) {
      // Enable dropdown and filter out ARRAY option
      this.questionForm.get('answerType')?.enable({ emitEvent: false });

      // If currently ARRAY, reset to NUMERIC
      const currentType = this.questionForm.get('answerType')?.value;
      if (currentType === LyfisAnswerType.ARRAY) {
        this.questionForm.patchValue(
          { answerType: LyfisAnswerType.NUMERIC },
          { emitEvent: false },
        );
      }

      // Clear arrayDefinitions
      this.arrayDefinitionsForm.clear();

      // Update form validation for current type
      this.updateFormValidationForCurrentAnswerType();
      // Restrict and prefill based on selected fact
      this.updateAnswerTypeRestrictionForFactIds(selectedFactIds, true);
    } else {
      // Enable dropdown and show all options except ARRAY
      this.questionForm.get('answerType')?.enable({ emitEvent: false });

      // If currently ARRAY, reset to NUMERIC
      const currentType = this.questionForm.get('answerType')?.value;
      if (currentType === LyfisAnswerType.ARRAY) {
        this.questionForm.patchValue(
          { answerType: LyfisAnswerType.NUMERIC },
          { emitEvent: false },
        );
      }

      // Clear arrayDefinitions
      this.arrayDefinitionsForm.clear();

      // Update form validation for current type
      this.updateFormValidationForCurrentAnswerType();
      // No restriction
      this.restrictedToOptionsOrYesNo = false;
    }
  }

  private updateAnswerTypeRestrictionForFactIds(
    factIds: string[],
    prefillOptions: boolean,
  ): void {
    if (factIds.length === 1) {
      const fact = this.ruleEngineFactsMap.get(factIds[0]);
      const hasAvailable = !!fact?.availableOptions?.length;
      this.restrictedToOptionsOrYesNo = hasAvailable;
      if (hasAvailable && prefillOptions && this.optionsForm.length === 0) {
        this.prefillOptionsFromAvailableOptions(fact!.availableOptions!);
        const t = this.questionForm.get('answerType')?.value as LyfisAnswerType;
        if (t !== LyfisAnswerType.OPTIONS && t !== LyfisAnswerType.YES_NO) {
          this.questionForm.patchValue(
            { answerType: LyfisAnswerType.OPTIONS },
            { emitEvent: false },
          );
          this.updateFormValidationForCurrentAnswerType();
        }
      }
    } else {
      this.restrictedToOptionsOrYesNo = false;
    }
  }

  rebuildArrayDefinitions(factIds: string[]): void {
    this.arrayDefinitionsForm.clear();
    factIds.forEach((factId) => {
      const factOption = this.factOptions.find((f) => f.id === factId);
      this.arrayDefinitionsForm.push(
        this.fb.group({
          placeHolder: ['', [Validators.required]],
          factKey: [factOption?.name || '', [Validators.required]],
          validation: this.fb.group({
            min: [null],
            max: [null],
            regex: [''],
          }),
        }),
      );
    });

    // Trigger validation update for array definitions
    this.arrayDefinitionsForm.updateValueAndValidity({ emitEvent: false });
  }

  onSave(): void {
    // Ensure form validation is up to date before checking validity
    this.updateFormValidationForCurrentAnswerType();

    console.log('Form value:', this.questionForm.value);
    console.log('Form valid:', this.questionForm.valid);
    console.log('Form errors:', this.getFormErrors());

    if (!this.questionForm.valid) {
      this.questionForm.markAllAsTouched();
      console.error('Form validation failed. Errors:', this.getFormErrors());
      return;
    }

    const raw = this.questionForm.getRawValue();
    const answerType = raw.answerType as LyfisAnswerType;

    // Convert factId array to comma-separated string for factKey
    const factKey = Array.isArray(raw.factId)
      ? raw.factId.join(',')
      : raw.factId;

    // build request per rules
    const request: any = {
      id: raw.id,
      internalLabel: raw.internalLabel,
      label: raw.label,
      order: raw.order,
      question: raw.question,
      questionDescription: raw.questionDescription,
      answerType: answerType,
      factId: factKey,
      diseaseIds: raw.diseaseIds,
      active: raw.active,
      cadence: raw.cadence?.minIntervalDays
        ? { minIntervalDays: raw.cadence.minIntervalDays }
        : undefined,
    };

    if (answerType === LyfisAnswerType.OPTIONS) {
      request.allowMultiple = raw.allowMultiple || false;
    }
    // Add placeholder for numeric and text_box types
    if (
      answerType === LyfisAnswerType.NUMERIC ||
      answerType === LyfisAnswerType.TEXT_BOX
    ) {
      request.placeholder = raw.placeholder;
    }

    // Add arrayDefinitions for array type
    if (answerType === LyfisAnswerType.ARRAY) {
      request.arrayDefinitions = (raw.arrayDefinitions || []).map(
        (def: any) => ({
          placeHolder: def.placeHolder,
          factKey: def.factKey,
          validation:
            def.validation?.min != null ||
              def.validation?.max != null ||
              def.validation?.regex
              ? {
                min: def.validation.min,
                max: def.validation.max,
                regex: def.validation.regex,
              }
              : undefined,
        }),
      );
    }

    if (this.shouldShowOptions()) {
      request.options = (raw.options || []).map((o: any) => ({
        key: o.key,
        label: o.label,
        order: o.order,
        isDefault: o.isDefault || false,
      }));
    }

    // Only add validation for non-array types
    if (this.shouldShowValidation() && answerType !== LyfisAnswerType.ARRAY) {
      const v = raw.validation || {};
      if (v.min != null || v.max != null || (v.regex && v.regex.length > 0)) {
        request.validation = {
          min: v.min ?? null,
          max: v.max ?? null,
          regex: v.regex ?? '',
        };
      }
    }

    console.log('Final request:', request);

    this.saving = true;
    this.lyfisSvc.upsertLyfisQuestion(request).subscribe({
      next: () => {
        this.saving = false;
        if (this.drawerMode === 'create') {
          this.resetForm();
        } else {
          // after update, go back to view mode with disabled fields
          this.drawerMode = 'view';
          this.questionForm.disable({ emitEvent: false });
        }
        this.fetch();
        this.createDrawerVisible = false;
      },
      error: (error) => {
        console.error('Error saving question:', error);
        this.saving = false;
      },
    });
  }

  getFormErrors(): any {
    const errors: any = {};

    Object.keys(this.questionForm.controls).forEach((key) => {
      const control = this.questionForm.get(key);
      if (control && control.errors) {
        errors[key] = control.errors;
      }
    });

    // Check array form errors
    if (this.arrayDefinitionsForm.errors) {
      errors['arrayDefinitions'] = this.arrayDefinitionsForm.errors;
    }

    this.arrayDefinitionsForm.controls.forEach((control, index) => {
      if (control.errors) {
        errors[`arrayDefinitions[${index}]`] = control.errors;
      }
      if (control instanceof FormGroup) {
        Object.keys(control.controls).forEach((subKey) => {
          const subControl = control.get(subKey);
          if (subControl && subControl.errors) {
            errors[`arrayDefinitions[${index}].${subKey}`] = subControl.errors;
          }
        });
      }
    });

    return errors;
  }

  resetForm(): void {
    this.questionForm.reset({
      id: undefined,
      internalLabel: '',
      label: '',
      order: 1,
      question: '',
      questionDescription: '',
      diseaseIds: [],
      answerType: LyfisAnswerType.NUMERIC,
      factId: [],
      validation: { min: null, max: null, regex: '' },
      active: true,
      cadence: { minIntervalDays: null },
      placeholder: '',
      allowMultiple: false,
    });
    this.optionsForm.clear();
    this.arrayDefinitionsForm.clear();
    // ensure controls enabled after reset
    const validationGroup = this.questionForm.get('validation') as FormGroup;
    Object.values(validationGroup.controls).forEach((c) =>
      c.enable({ emitEvent: false }),
    );
  }

  getDiseaseNames(diseaseIds: string[]): string[] {
    return this.diseaseOptions
      .filter((d) => diseaseIds.includes(d.id))
      .map((d) => d.name);
  }

  protected override buildRequest(
    exportFlag = false,
  ): LyfisQuestionsGridRequest {
    const req: LyfisQuestionsGridRequest = {
      page: this.page,
      pageSize: this.pageSize,
      export: exportFlag,
      filters: {},
      searchQuery: {},
      sort: {},
    } as unknown as LyfisQuestionsGridRequest;

    // active filter
    if (typeof this.activeFilter === 'boolean') {
      (req.filters as Record<string, string[] | undefined>)[
        LyfisQuestionsFilter.ACTIVE
      ] = [String(this.activeFilter)];
    }

    // answer type filter
    if (this.selectedAnswerTypes?.length) {
      (req.filters as Record<string, string[] | undefined>)[
        LyfisQuestionsFilter.ANSWER_TYPE
      ] = [...this.selectedAnswerTypes];
    }
    if (this.selectedDiseaseIds?.length) {
      (req.filters as Record<string, string[] | undefined>)[
        LyfisQuestionsFilter.DISEASE_ID
      ] = [...this.selectedDiseaseIds];
    }

    // internal label search
    if (
      this.internalLabelSearch &&
      this.internalLabelSearch.trim().length > 0
    ) {
      (req.searchQuery as Record<string, string | undefined>)[
        LyfisQuestionsSearch.INTERNAL_LABEL
      ] = this.internalLabelSearch.trim();
    }

    // order sort
    if (this.orderSort) {
      (req.sort as Record<string, AnyoSortDirection | undefined>)[
        LyfisQuestionsSort.ORDER
      ] = this.orderSort;
    }

    return req;
  }

  protected fetchGridData(
    request: LyfisQuestionsGridRequest,
  ): Observable<GridResponse<undefined, LyfisQuestion>> {
    return this.lyfisSvc.getQuestionsGrid(request) as unknown as Observable<
      GridResponse<undefined, LyfisQuestion>
    >;
  }

  protected override applyRequestToState(req: LyfisQuestionsGridRequest): void {
    super.applyRequestToState(req);
    // restore active filter
    if (req.filters) {
      const activeArr = (req.filters as Record<string, string[] | undefined>)[
        LyfisQuestionsFilter.ACTIVE
      ];
      if (Array.isArray(activeArr) && activeArr.length) {
        this.activeFilter = activeArr[0] === 'true';
      } else {
        this.activeFilter = false;
      }

      const answerTypeArr = (
        req.filters as Record<string, string[] | undefined>
      )[LyfisQuestionsFilter.ANSWER_TYPE];
      if (Array.isArray(answerTypeArr)) {
        this.selectedAnswerTypes = [...answerTypeArr];
        this.answerTypeTemp = [...answerTypeArr];
      } else {
        this.selectedAnswerTypes = [];
        this.answerTypeTemp = [];
      }
      const diseaseIdArr = (
        req.filters as Record<string, string[] | undefined>
      )[LyfisQuestionsFilter.DISEASE_ID];
      if (Array.isArray(diseaseIdArr)) {
        this.selectedDiseaseIds = [...diseaseIdArr];
        this.diseaseTemp = [...diseaseIdArr];
      } else {
        this.selectedDiseaseIds = [];
        this.diseaseTemp = [];
      }
    }

    // restore internal label search
    if (req.searchQuery) {
      const val = (req.searchQuery as Record<string, string | undefined>)[
        LyfisQuestionsSearch.INTERNAL_LABEL
      ];
      this.internalLabelSearch = val || '';
      this.internalLabelTemp = val || '';
    } else {
      this.internalLabelSearch = '';
      this.internalLabelTemp = '';
    }

    // restore order sort
    if (req.sort) {
      const dir = (req.sort as Record<string, AnyoSortDirection | undefined>)[
        LyfisQuestionsSort.ORDER
      ];
      this.orderSort = dir;
    } else {
      this.orderSort = undefined;
    }
  }

  // UI handlers
  onActiveToggle(val: boolean): void {
    this.activeFilter = val;
    this.page = 1;
    this.fetch();
  }

  onAnswerTypeTempChange(values: string[]): void {
    this.answerTypeTemp = values || [];
  }

  applyAnswerTypeFilter(): void {
    this.selectedAnswerTypes = [...this.answerTypeTemp];
    this.page = 1;
    this.fetch();
  }

  resetAnswerTypeFilter(): void {
    this.answerTypeTemp = [];
    this.selectedAnswerTypes = [];
    this.page = 1;
    this.fetch();
  }

  applyInternalLabelSearch(): void {
    this.internalLabelSearch = this.internalLabelTemp.trim();
    this.page = 1;
    this.fetch();
  }

  resetInternalLabelSearch(): void {
    this.internalLabelTemp = '';
    this.internalLabelSearch = '';
    this.page = 1;
    this.fetch();
  }

  onOrderSortChange(dir?: AnyoSortDirection): void {
    this.orderSort = dir;
    this.page = 1;
    this.fetch();
  }

  onDiseaseTempChange(values: string[]): void {
    this.diseaseTemp = values || [];
  }

  applyDiseaseFilter(): void {
    this.selectedDiseaseIds = [...this.diseaseTemp];
    this.page = 1;
    this.fetch();
  }

  resetDiseaseFilter(): void {
    this.diseaseTemp = [];
    this.selectedDiseaseIds = [];
    this.page = 1;
    this.fetch();
  }
}
